import { motion } from "framer-motion";
import { Home, Factory, BatteryCharging, Zap, ArrowUpRight } from "lucide-react";

const services = [
  {
    id: "pv-privat",
    title: "PV-Anlagen Privat",
    desc: "Massgeschneiderte Photovoltaik für Einfamilienhäuser, MFH und Villen – mit Schweizer Modulen und maximalem Eigenverbrauch.",
    icon: Home,
    tag: "3–30 kWp",
    img: "/images/service-pv-privat.webp",
    alt: "Schweizer Einfamilienhaus mit vollflächig schwarzer Photovoltaik-Anlage auf dem Satteldach",
    layout: "feature",
    span: "md:col-span-2 md:row-span-2",
  },
  {
    id: "pv-gewerbe",
    title: "PV-Anlagen Gewerbe",
    desc: "Grossanlagen bis 1 MWp für Industrie, Logistik und Landwirtschaft – schlüsselfertig geplant und gebaut.",
    icon: Factory,
    tag: "bis 1 MWp",
    img: "/images/service-pv-gewerbe.webp",
    alt: "Luftaufnahme eines Industrieflachdachs mit langen Reihen von Solarmodulen",
    layout: "feature",
    span: "md:col-span-2",
  },
  {
    id: "speicher",
    title: "Stromspeicher",
    desc: "Lithium-Speicher von 5 bis 30 kWh – inkl. Notstromfunktion und App-Steuerung.",
    icon: BatteryCharging,
    tag: "5–30 kWh",
    img: "/images/service-speicher.webp",
    alt: "Wandmontierter Hausstromspeicher neben einem Wechselrichter im Technikraum",
    layout: "stack",
    span: "",
  },
  {
    id: "wallbox",
    title: "Wallboxen & E-Mobilität",
    desc: "Intelligente Ladestationen mit PV-Überschussladen direkt vom eigenen Dach.",
    icon: Zap,
    tag: "11–22 kW",
    img: "/images/service-wallbox.webp",
    alt: "Elektroauto lädt an einer Wallbox in einem Carport mit Solardach",
    layout: "stack",
    span: "",
  },
];

export default function Services() {
  return (
    <section
      id="leistungen"
      data-testid="services-section"
      className="relative py-20 md:py-28"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-12">
          <div className="lg:col-span-7">
            <div className="text-sm text-[#EAB308] font-semibold uppercase tracking-widest">
              Unsere Leistungen
            </div>
            <h2 className="mt-3 font-display font-semibold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1A1D20]">
              Alles für Ihre Energiezukunft –
              <span className="text-[#4B5563]"> aus einer Hand.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:text-right">
            <p className="text-[#4B5563] max-w-md lg:ml-auto">
              Wir kombinieren Solarmodule, Speicher und Ladelösungen zu einem
              perfekt abgestimmten System – mit transparenter Beratung von der
              ersten Skizze bis zur Inbetriebnahme.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-5 md:auto-rows-[340px]">
          {services.map((s, idx) => (
            <motion.a
              key={s.id}
              href="#kontakt"
              data-testid={`service-card-${s.id}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5, delay: idx * 0.08 }}
              className={`service-tile card-lift group relative rounded-2xl border border-black/5 overflow-hidden ${s.span} ${
                s.layout === "feature" ? "min-h-[340px]" : "flex flex-col bg-white"
              }`}
            >
              {s.layout === "feature" ? (
                <FeatureCard service={s} />
              ) : (
                <StackCard service={s} />
              )}
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
}

/** Grossformat: Bild vollflächig, Inhalt auf dunklem Verlauf. */
function FeatureCard({ service: s }) {
  const Icon = s.icon;
  return (
    <>
      <img
        src={s.img}
        alt={s.alt}
        loading="lazy"
        decoding="async"
        className="absolute inset-0 w-full h-full object-cover img-placeholder transition-transform duration-700 group-hover:scale-105"
      />
      {/* Flächiger Scrim plus Verlauf – hält den Text auch auf hellen Motiven lesbar */}
      <div className="absolute inset-0 bg-[#0F1113]/35" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0F1113] via-[#0F1113]/72 to-transparent" />

      <div className="relative h-full p-6 md:p-8 flex flex-col justify-between">
        <div className="flex items-start justify-between">
          <div className="w-12 h-12 rounded-xl bg-[#EAB308] text-[#1A1D20] flex items-center justify-center">
            <Icon size={22} aria-hidden="true" />
          </div>
          <span className="text-[11px] font-semibold tracking-wider uppercase px-2.5 py-1 rounded-full bg-[#0F1113]/70 text-white backdrop-blur-sm">
            {s.tag}
          </span>
        </div>

        <div className="mt-8">
          <h3 className="font-display font-semibold text-xl md:text-2xl tracking-tight text-white">
            {s.title}
          </h3>
          <p className="mt-3 text-sm leading-relaxed text-white/85 max-w-md">
            {s.desc}
          </p>
          <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#EAB308]">
            Mehr erfahren <ArrowUpRight size={16} aria-hidden="true" />
          </span>
        </div>
      </div>
    </>
  );
}

/** Kleinformat: Bildband oben, Inhalt auf heller Fläche. */
function StackCard({ service: s }) {
  const Icon = s.icon;
  return (
    <>
      <div className="relative h-[132px] shrink-0 overflow-hidden img-placeholder">
        <img
          src={s.img}
          alt={s.alt}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <span className="absolute top-3 right-3 text-[11px] font-semibold tracking-wider uppercase px-2.5 py-1 rounded-full bg-[#1A1D20]/75 text-white backdrop-blur-sm">
          {s.tag}
        </span>
        <div className="absolute -bottom-5 left-5 w-11 h-11 rounded-xl bg-[#1A1D20] text-[#EAB308] flex items-center justify-center border-2 border-white">
          <Icon size={20} aria-hidden="true" />
        </div>
      </div>

      <div className="flex-1 p-5 pt-9 flex flex-col text-[#1A1D20]">
        <h3 className="font-display font-semibold text-lg tracking-tight">
          {s.title}
        </h3>
        <p className="mt-2 text-sm leading-relaxed text-[#4B5563] flex-1">
          {s.desc}
        </p>
        <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#1A1D20]">
          Mehr erfahren <ArrowUpRight size={16} aria-hidden="true" />
        </span>
      </div>
    </>
  );
}
